import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  viewChild
} from '@angular/core';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { EllipsisTooltipDirective } from '@simOn/ui/sim-tooltip';
import { SimHistoryPopoverGroup } from './sim-history-popover.model';

/**
 * A history list in a popover over its container (dimmed backdrop below it): a title with a "new item" action,
 * grouped rows with the current one highlighted, and an empty state with its own action. It fills its nearest
 * positioned ancestor, so put it inside a `position: relative` element. All texts come from the consumer.
 */
@Component({
  selector: 'sim-history-popover',
  imports: [SimIconModule, SimSpinnerComponent, EllipsisTooltipDirective],
  templateUrl: './sim-history-popover.component.html',
  styleUrl: './sim-history-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'sim-history-popover' }
})
export class SimHistoryPopoverComponent {
  readonly title = input.required<string>();
  readonly groups = input.required<readonly SimHistoryPopoverGroup[]>();
  /** Id of the highlighted row; `null` when none is current. */
  readonly currentId = input<string | null>(null);
  readonly currentLabel = input<string>('');
  readonly newItemLabel = input<string>('');
  readonly emptyTitle = input<string>('');
  readonly emptyText = input<string>('');
  readonly emptyActionLabel = input<string>('');
  /** Accessible name of the spinner of a busy item. */
  readonly busyLabel = input<string>('');

  readonly selected = output<string>();
  readonly newItem = output<void>();
  /** Backdrop click or Escape. */
  readonly closed = output<void>();

  private readonly _panel = viewChild.required<ElementRef<HTMLElement>>('panel');

  constructor() {
    // Focus moves into the popover so Escape closes it right away.
    afterNextRender(() => this._panel().nativeElement.focus());
  }

  protected onSelect(id: string): void {
    this.selected.emit(id);
  }

  protected onNewItem(): void {
    this.newItem.emit();
  }

  protected onClose(): void {
    this.closed.emit();
  }
}
